
import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Button,
  notification,
  Form,
  Input,
  Select,
  DatePicker,
  TimePicker,
  Space,
} from "antd";
import { SendOutlined, SaveOutlined } from '@ant-design/icons';
import axios from "axios";
import moment from 'moment';
import { baseUrl } from "../../config";

const { Item } = Form;
const { Option } = Select;
const { TextArea } = Input;

const branchOptions = [
  "अखिल भारतीय राजपुरोहित समाज विकास संस्थान",
  "श्री खेतेश्वर गुरुकुल, मायलावास",
  "श्री खेतेश्वर गुरुकुल, बिजरोल खेड़ा",
  "श्री खेतेश्वर गुरुकुल, सायला",
  "श्री खेतेश्वर गुरुकुल, सवाई छोटी",
  "श्री खेतेश्वर भवन, हरिद्वार"
];

function VisitorForm({ userId, onRefreshData = () => {} }) {
  const [form] = Form.useForm();
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visitDate, setVisitDate] = useState(null);
  const [timeInDate, setTimeInDate] = useState(null);
  const [timeOutDate, setTimeOutDate] = useState(null);
  const [sendMessage, setSendMessage] = useState(false);

  useEffect(() => {
    if (userId) {
      setIsUpdateMode(true);
      fetchVisitorDetails();
    } else {
      setIsUpdateMode(false);
      form.resetFields();
      setVisitDate(null);
      setTimeInDate(null);
      setTimeOutDate(null);
    }
  }, [userId]);

  const fetchVisitorDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/visitor/read/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (response.status === 200) {
        const data = response.data.result;

        if (data.visit_date && data.visit_date !== '0000-00-00' && moment(data.visit_date).isValid()) {
          setVisitDate(data.visit_date);
        } else {
          setVisitDate(null);
        }

        if (data.visit_time_in && data.visit_time_in !== "00:00:00") {
          setTimeInDate(moment(data.visit_time_in, "HH:mm:ss"));
        } else {
          setTimeInDate(null);
        }

        if (data.visit_time_out && data.visit_time_out !== "00:00:00") {
          setTimeOutDate(moment(data.visit_time_out, "HH:mm:ss"));
        } else {
          setTimeOutDate(null);
        }

        form.setFieldsValue({
          ...data,
          visit_time_in: data.visit_time_in && data.visit_time_in !== "00:00:00"
            ? moment(data.visit_time_in, "HH:mm:ss") : null,
          visit_time_out: data.visit_time_out && data.visit_time_out !== "00:00:00"
            ? moment(data.visit_time_out, "HH:mm:ss") : null,
        });
      }
    } catch (error) {
      notification.error({ message: 'Error', description: 'Failed to fetch visitor details', placement: 'topRight' });
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);

      if (visitDate && visitDate !== '0000-00-00') {
        values.visit_date = moment(visitDate).format('YYYY-MM-DD');
      }
      if (timeInDate) {
        values.visit_time_in = moment(timeInDate).format('HH:mm:ss');
      }
      if (timeOutDate) {
        values.visit_time_out = moment(timeOutDate).format('HH:mm:ss');
      }
      values.action = sendMessage ? 'save_send' : 'save';

      let response;
      if (isUpdateMode) {
        response = await axios.patch(`${baseUrl}/visitor/update/${userId}`, values, {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        });
      } else {
        response = await axios.post(`${baseUrl}/visitor/create`, values, {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        });
      }

      if (response.status === 200) {
        const actionText = sendMessage
          ? (isUpdateMode ? 'updated and message sent' : 'created and message sent')
          : (isUpdateMode ? 'updated' : 'created');
        notification.success({
          message: 'Success',
          description: `Visitor record ${actionText} successfully!`,
          placement: 'topRight'
        });
        onRefreshData();
        if (!isUpdateMode) {
          form.resetFields();
          setVisitDate(null);
          setTimeInDate(null);
          setTimeOutDate(null);
        }
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Operation failed',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
      initialValues={{ visit_branch: "श्री खेतेश्वर गुरुकुल, मायलावास" }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
        Visitor Details
      </div>
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12}>
          <Item
            label="Phone Number"
            name="phone_no"
            rules={[
              { required: true, message: 'Please enter phone number' },
              { pattern: /^\d{10,12}$/, message: 'Phone must be 10-12 digits' }
            ]}
          >
            <Input placeholder="Enter phone number" type="number" />
          </Item>
        </Col>

        <Col xs={24} sm={12}>
          <Item
            label="Visitor Name"
            name="name"
            rules={[{ required: true, message: 'Please enter visitor name' }]}
          >
            <Input placeholder="Enter visitor name" />
          </Item>
        </Col>

        <Col xs={24} sm={12}>
          <Item label="Purpose of Visit" name="purpose_visit">
            <Input placeholder="Enter purpose of visit" />
          </Item>
        </Col>

        <Col xs={24} sm={12}>
          <Item label="Visit Branch" name="visit_branch">
            <Select placeholder="Select branch" showSearch
              filterOption={(input, option) =>
                option.value.toLowerCase().includes(input.toLowerCase())
              }
            >
              {branchOptions.map((branch) => (
                <Option key={branch} value={branch}>{branch}</Option>
              ))}
            </Select>
          </Item>
        </Col>

        <Col xs={24} sm={8}>
          <Item label="Visit Date">
            <DatePicker
              onChange={(date) => setVisitDate(date ? moment(date).format('YYYY-MM-DD') : null)}
              value={visitDate && visitDate !== "0000-00-00" && moment(visitDate).isValid() ? moment(visitDate) : null}
              format="DD-MM-YYYY"
              placeholder="Select date"
              style={{ width: '100%' }}
              allowClear
            />
          </Item>
        </Col>

        <Col xs={12} sm={8}>
          <Item label="Time In" name="visit_time_in">
            <TimePicker
              use12Hours
              format="h:mm A"
              value={timeInDate}
              onChange={(time) => setTimeInDate(time)}
              placeholder="Time in"
              style={{ width: '100%' }}
            />
          </Item>
        </Col>

        <Col xs={12} sm={8}>
          <Item label="Time Out" name="visit_time_out">
            <TimePicker
              use12Hours
              format="h:mm A"
              value={timeOutDate}
              onChange={(time) => setTimeOutDate(time)}
              placeholder="Time out"
              style={{ width: '100%' }}
            />
          </Item>
        </Col>

        <Col xs={24}>
          <Item label="Remarks" name="remark">
            <TextArea placeholder="Enter remarks (optional)" rows={3} maxLength={500} showCount />
          </Item>
        </Col>
      </Row>

      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
        <Button onClick={onRefreshData} style={{ borderRadius: 8 }}>Cancel</Button>
        <Button htmlType="submit" loading={loading} icon={<SaveOutlined />} onClick={() => setSendMessage(false)} style={{ borderRadius: 8, borderColor: '#7a0000', color: '#7a0000' }}>
          {isUpdateMode ? 'Update' : 'Save'}
        </Button>
        <Button type="primary" htmlType="submit" loading={loading} icon={<SendOutlined />} onClick={() => setSendMessage(true)} style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8 }}>
          {isUpdateMode ? 'Update & Send' : 'Save & Send'}
        </Button>
      </div>
    </Form>
  );
}

export default VisitorForm;
